import React from 'react';
import '../../styles/home/PartnerLifeSection.css';

interface Step {
  number: string;
  tag: string;
  heading: string;
  description: string;
  badge: string;
  image: string;
}

const steps: Step[] = [
  {
    number: '01',
    tag: 'RETAILER ORDERS',
    heading: 'A few taps.\nOrder confirmed.',
    description:
      'Browse the full catalog, set quantities, and place your order through the VIN portal or by voice via VIN Pulse OS. Smart reorder suggestions mean you\'re never caught short.',
    badge: 'Smart reorder suggestions active',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600',
  },
  {
    number: '02',
    tag: 'WAREHOUSE PREPARES',
    heading: 'Packed with\nprecision, fast.',
    description:
      'Your order hits our distribution hub instantly. Picked, quality-checked, and loaded — VIN Pulse OS optimizes every route for maximum efficiency before the van leaves.',
    badge: 'Order #VIN-0427 — Picking in progress',
    image: 'https://images.unsplash.com/photo-1553413077-190dd305871c?w=600',
  },
  {
    number: '03',
    tag: 'DELIVERY ARRIVES',
    heading: 'On route.\nOn time. Always.',
    description:
      'Follow your driver in real time — live GPS, estimated arrival window, and an alert the moment they\'re outside. No more chasing, no more uncertainty.',
    badge: 'Route 7 — ETA 14:30, 3 stops away',
    image: 'https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?w=600',
  },
  {
    number: '04',
    tag: 'SHELVES STOCKED',
    heading: 'Full shelves.\nHappy customers.',
    description:
      'Goods arrive, shelves are restocked, and VIN Pulse OS automatically updates your inventory and queues the next recommended reorder — closing the loop before you need to think about it.',
    badge: 'Delivery confirmed — Inventory updated',
    image: 'https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=600',
  },
];

const PartnerLifeSection: React.FC = () => {
  return (
    <section className="partner-life">
      {/* Header */}
      <div className="partner-life__header">
        <span className="partner-life__tag">HOW IT WORKS</span>
        <h2 className="partner-life__heading">
          A day in the life of a<br />
          <span className="partner-life__heading--accent">VIN partner</span>
        </h2>
        <p className="partner-life__subtitle">
          From first tap to stocked shelf — here's the journey, in four chapters.
        </p>
      </div>

      {/* Steps */}
      <div className="partner-life__steps">
        {steps.map((step, index) => {
          const isEven = index % 2 === 1;
          return (
            <div
              key={step.number}
              className={`partner-life__step ${isEven ? 'partner-life__step--reversed' : ''}`}
            >
              <div className="partner-life__step-content">
                <div className="partner-life__step-meta">
                  <span className="partner-life__step-number">{step.number}</span>
                  <span className="partner-life__step-tag">{step.tag}</span>
                </div>
                <h3 className="partner-life__step-heading">
                  {step.heading.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < step.heading.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </h3>
                <p className="partner-life__step-desc">{step.description}</p>
                <div className="partner-life__step-badge">
                  <span className="partner-life__step-badge-dot" />
                  {step.badge}
                </div>
              </div>
              <div className="partner-life__step-image-wrap">
                <img
                  src={step.image}
                  alt={step.tag}
                  className="partner-life__step-image"
                  loading="lazy"
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PartnerLifeSection;
